import type { DraftState, WizardItemType, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { IUPAC_PRIMER, cleanPrimer } from '../../Util/orderSettingDetection/iupac.ts'
import { illuminaSampleNumber, wellPosition } from '../../Util/orderSettingDetection/plateDetection.ts'
import type { PrimerComparison } from './primerComparison.ts'
import { comparePrimer } from './primerComparison.ts'
import { CONFIDENCE_TEXT, ESTIMATE_NEXT_STEP, estimateLines, sideBasis } from './estimateExplanation.ts'
import { declaredPrimers, primersFor, sheetPrimersFor } from './sheet/applyPlan.ts'
import { hasPrimers } from './primerCheck.ts'
import { placedGroups } from './pools.ts'
import { sampleDisplayName, sampleNameSource } from './sampleLabels.ts'
import type { WrittenOrientation } from './primerOrientation.ts'
import { writtenEstimate, writtenOrientation } from './primerOrientation.ts'

export const PRIMER_SIDES = ['forward', 'reverse'] as const
export type PrimerSide = (typeof PRIMER_SIDES)[number]

export const PRIMER_GRID_COLUMNS = ['sample', 'file', 'well', 'run', 'forward', 'reverse', 'source', 'confidence'] as const
export type PrimerGridColumn = (typeof PRIMER_GRID_COLUMNS)[number]

export const PRIMER_GRID_TITLES: Record<PrimerGridColumn, string> = {
  sample: 'Customer sample name',
  file: 'File',
  well: 'Well',
  run: 'Run / S-number',
  forward: 'Forward',
  reverse: 'Reverse',
  source: 'Source',
  confidence: 'Confidence'
}

export const isPrimerSide = (column: string): column is PrimerSide => column === 'forward' || column === 'reverse'

/** What the lab typed or pasted, as the item will carry it: upper case, without blanks and dashes. */
export const normalizePrimer = (value: string): string => value.toUpperCase().replace(/[\s-]/g, '')

export const isPrimer = (value: string): boolean => IUPAC_PRIMER.test(normalizePrimer(value))

export type PrimerCell = {
  /** What the item gets on this side; a tentative estimate is shown but not written. `''`: nothing yet. */
  value: string
  tentative: boolean
  /** The bases in front of the gene primer, highlighted. */
  tag: string
  origin?: 'manual' | 'sheet' | 'estimate'
  /** A given primer that is not one (12+ IUPAC bases). */
  invalid: boolean
  /** The given primer against the reads' estimate. */
  comparison: PrimerComparison
  details: string[]
}

export type PrimerGridRow = {
  key: string
  sample: string
  /** Whether the customer sample name is the sheet's or the file name's; absent when none is written. */
  sampleSource?: 'sheet' | 'file'
  itemType: WizardItemType
  file: string
  well: string
  run: string
  forward: PrimerCell
  reverse: PrimerCell
  source: string
  sourceTone: 'quiet' | 'warning' | 'error'
  confidence: string
  /** How the reads gave the primers, or why they gave none, in plain words. */
  basis: string[]
  attention: boolean
}

const COMPARISON_NOTE: Partial<Record<PrimerComparison['kind'], string>> = {
  agree: 'the reads agree',
  'gene-spelling': 'same tag, gene part spelled differently'
}

const comparisonNote = (comparison: PrimerComparison): string | undefined => {
  switch (comparison.kind) {
    case 'tag-mismatch':
      return `the reads carry tag ${comparison.readsTag || '(none)'}, this primer ${comparison.sheetTag}`
    case 'sheet-untagged':
      return `no tag in this primer; the reads carry ${comparison.readsTag}`
    default:
      return COMPARISON_NOTE[comparison.kind]
  }
}

const tagOf = (value: string, genes: readonly (string | null | undefined)[]): string => {
  const clean = cleanPrimer(value)
  if (clean !== value) {
    return ''
  }
  const gene = genes
    .map((candidate) => (candidate ? cleanPrimer(candidate) : ''))
    .filter((candidate) => candidate && clean.length > candidate.length && clean.endsWith(candidate))
    .sort((a, b) => b.length - a.length)[0]
  return gene ? clean.slice(0, clean.length - gene.length) : ''
}

const ORIGIN_TEXT = { manual: 'entered', sheet: 'sheet', estimate: 'estimated from reads' } as const

const ORIENTATION_TEXT: Record<WrittenOrientation['basis'], string> = {
  'earlier-orders': 'forward and reverse as on your earlier orders',
  registry: 'forward and reverse as the pair is registered',
  reads: 'forward is the primer the reads start with'
}

const cellOf = (draft: DraftState, group: WizardSampleGroup, side: PrimerSide): PrimerCell => {
  const estimate = writtenEstimate(draft, group)?.[side]
  const manual = group.manualPrimers?.[side]
  const declared = declaredPrimers(draft, group)[side]
  const written = primersFor(draft, group)[side]
  const origin = manual ? 'manual' : declared ? 'sheet' : written ? 'estimate' : undefined
  const tentativeEstimate = !written && estimate?.primer && estimate.confidence === 'low' ? estimate.primer : undefined
  const value = written ?? tentativeEstimate ?? ''
  const pair = draft.discovery?.primers.status === 'found' ? draft.discovery.primers.pair : undefined
  const variants = [...(pair?.firstVariants ?? []), ...(pair?.secondVariants ?? [])]
  const tag = origin === 'estimate' || tentativeEstimate ? (estimate?.prefix ?? '') : value ? tagOf(value, [estimate?.gene, ...variants]) : ''
  const comparison = comparePrimer(estimate, declared)
  const sheetValue = manual ? sheetPrimersFor(draft, group)[side] : undefined
  const details = [
    origin && `${ORIGIN_TEXT[origin]}: ${value}`,
    estimate && `reads (${CONFIDENCE_TEXT[estimate.confidence]}): ${sideBasis(estimate, side)}`,
    estimate?.primer && (estimate.spelledByLab ? 'gene part spelled as on your earlier orders' : 'gene part spelled as registered'),
    origin === 'estimate' && ORIENTATION_TEXT[writtenOrientation(draft, group).basis],
    manual && sheetValue && `clear the cell to go back to the sheet’s ${sheetValue}`,
    comparisonNote(comparison)
  ].filter((line): line is string => !!line)
  return {
    value,
    tentative: !!tentativeEstimate,
    tag,
    origin,
    invalid: !!declared && !IUPAC_PRIMER.test(cleanPrimer(declared)),
    comparison,
    details
  }
}

const DIAGNOSIS_TEXT: Partial<Record<string, string>> = {
  TAG_MISMATCH: 'tag mismatch',
  OTHER_PRIMER: 'another primer pair',
  ONE_PRIMER_MISSING: 'one primer wrong',
  PRIMERS_ABSENT: 'primers absent'
}

const SOURCE_TEXT = { sheet: 'from sheet', manual: 'entered', estimate: 'estimated from reads' } as const

const sourceOf = (draft: DraftState, group: WizardSampleGroup): Pick<PrimerGridRow, 'source' | 'sourceTone'> => {
  if (group.applied?.status === 'failed') {
    return { source: 'not written', sourceTone: 'error' }
  }
  const source = primersFor(draft, group).source
  const estimate = group.estimate
  const text = group.primersOnServer
    ? 'in order'
    : source
      ? SOURCE_TEXT[source]
      : !estimate
        ? '—'
        : estimate.forward.primer || estimate.reverse.primer
          ? 'estimate, not written'
          : 'not estimated'
  const diagnosis = group.diagnosis ? DIAGNOSIS_TEXT[group.diagnosis.diagnosis] : undefined
  return diagnosis ? { source: `${text} · ${diagnosis}`, sourceTone: 'warning' } : { source: text, sourceTone: 'quiet' }
}

const CONFIDENCE_RANK = { none: 0, low: 1, high: 2 } as const

const confidenceOf = (draft: DraftState, group: WizardSampleGroup): Pick<PrimerGridRow, 'confidence' | 'basis'> => {
  const estimate = writtenEstimate(draft, group)
  if (!estimate) {
    return { confidence: draft.discovery?.primers.status === 'found' ? 'reading…' : '—', basis: [] }
  }
  const worst = [estimate.forward.confidence, estimate.reverse.confidence].sort((a, b) => CONFIDENCE_RANK[a] - CONFIDENCE_RANK[b])[0]
  return {
    confidence: worst,
    basis: [...estimateLines(estimate), ...(hasPrimers(draft, group) ? [] : [ESTIMATE_NEXT_STEP])]
  }
}

const wellOf = (name: string): string => {
  const well = wellPosition(name)
  return well ? `${'ABCDEFGH'[well.row]}${String(well.column).padStart(2, '0')}` : ''
}

const runOf = (name: string, run: string | undefined): string => {
  const number = illuminaSampleNumber(name)
  return [number !== null ? `S${number}` : '', run ?? ''].filter(Boolean).join(' · ')
}

const NO_FLAGS: ReadonlySet<string> = new Set()

/** One row per placed item, each primer side as the item gets it, and whether the row needs the lab. */
export const primerGridRows = (draft: DraftState, flagged: ReadonlySet<string> = NO_FLAGS): PrimerGridRow[] =>
  placedGroups(draft).map((group) => {
    const file = draft.files[group.forward]?.name ?? ''
    const forward = cellOf(draft, group, 'forward')
    const reverse = cellOf(draft, group, 'reverse')
    const source = sourceOf(draft, group)
    const disagrees = [forward, reverse].some(
      (cell) => cell.invalid || cell.comparison.kind === 'tag-mismatch' || cell.comparison.kind === 'sheet-untagged'
    )
    const sampleSource = sampleNameSource(draft, group.key)
    return {
      key: group.key,
      sample: sampleDisplayName(draft, group.key),
      ...(sampleSource ? { sampleSource } : {}),
      itemType: group.itemType ?? 'SAMPLE',
      file,
      well: wellOf(file),
      run: runOf(file, group.run),
      forward,
      reverse,
      ...source,
      ...confidenceOf(draft, group),
      attention: flagged.has(group.key) || !hasPrimers(draft, group) || disagrees || source.sourceTone !== 'quiet'
    }
  })

/** Rows whose primers, on either side, are the reads' estimate rather than given by the lab. */
export const estimatedRows = (rows: readonly PrimerGridRow[]): PrimerGridRow[] =>
  rows.filter((row) => [row.forward, row.reverse].some((cell) => cell.origin === 'estimate' || cell.tentative))

/** The rows as text per column, which the classic grid's sort and filter work on. */
export const sortableRows = (rows: readonly PrimerGridRow[]): Record<PrimerGridColumn, string>[] =>
  rows.map((row) => ({
    sample: row.sample,
    file: row.file,
    well: row.well,
    run: row.run,
    forward: row.forward.value,
    reverse: row.reverse.value,
    source: row.source,
    confidence: row.confidence
  }))
